import React, { useState } from 'react';
import {
  Home,
  Users,
  UserCircle,
  ClipboardList,
  Calendar,
  Settings,
  Wand2,
  Globe,
  FileText,
  Menu,
  RepeatIcon,
  Receipt,
  CreditCard,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

export type Route = 
  | 'dashboard' 
  | 'customers' 
  | 'team'
  | 'work-orders' 
  | 'schedule' 
  | 'settings' 
  | 'work-magic' 
  | 'site-maker'
  | 'invoices'
  | 'recurring-orders'
  | 'expenses'
  | 'payments';

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const [currentRoute, setCurrentRoute] = React.useState<Route>('dashboard');
  const [openSections, setOpenSections] = useState<string[]>(['Core', 'Service', 'Accounting', 'Tools']);

  // Update current route from URL on mount
  React.useEffect(() => {
    const path = window.location.pathname.substring(1);
    if (path && !path.startsWith('site/')) {
      setCurrentRoute(path as Route);
    }
  }, []);

  const handleNavigation = (route: Route) => {
    setCurrentRoute(route);
    window.history.pushState({}, '', `/${route === 'dashboard' ? '' : route}`);
    window.dispatchEvent(new CustomEvent('routeChange', { detail: route }));
  };

  const toggleSection = (title: string) => {
    setOpenSections(prev => 
      prev.includes(title) 
        ? prev.filter(t => t !== title)
        : [...prev, title]
    );
  };

  const navSections = [
    {
      title: 'Core',
      items: [
        {
          name: 'Dashboard',
          icon: Home,
          route: 'dashboard',
        },
        {
          name: 'Customers',
          icon: Users,
          route: 'customers',
        },
        {
          name: 'Team',
          icon: UserCircle,
          route: 'team',
        },
      ],
    },
    {
      title: 'Service',
      items: [
        {
          name: 'Work Orders',
          icon: ClipboardList,
          route: 'work-orders',
        },
        {
          name: 'Schedule',
          icon: Calendar,
          route: 'schedule',
        },
      ],
    },
    {
      title: 'Accounting',
      items: [
        {
          name: 'Invoices',
          icon: FileText,
          route: 'invoices',
          badge: 'New',
        },
        {
          name: 'Payments',
          icon: CreditCard,
          route: 'payments',
          badge: 'New',
        },
        {
          name: 'Recurring Orders',
          icon: RepeatIcon,
          route: 'recurring-orders',
          badge: 'New',
        },
        {
          name: 'Expenses',
          icon: Receipt,
          route: 'expenses',
          badge: 'New',
        },
      ],
    },
    {
      title: 'Tools',
      items: [
        {
          name: 'Work Magic',
          icon: Wand2,
          route: 'work-magic',
        },
        {
          name: 'Site Builder',
          icon: Globe,
          route: 'site-maker',
        },
        {
          name: 'Settings',
          icon: Settings,
          route: 'settings',
        },
      ],
    },
  ];

  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 z-30 flex h-screen flex-col border-r transition-all duration-300",
        collapsed ? "w-16" : "w-64",
        "bg-[#efeee6]"
      )}
    >
      {/* Header */}
      <div className="flex h-16 items-center justify-between px-4 border-b border-black/10">
        {!collapsed && (
          <div className="logo-container w-8 h-8 flex items-center justify-center">
            <span className="logo-text text-lg">S</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onCollapse(!collapsed)}
          className={cn(
            "text-gray-500 hover:text-gray-900 hover:bg-black/5",
            collapsed && "ml-auto"
          )}
        >
          <Menu className="h-5 w-5" />
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <nav className="space-y-1 p-2">
          {navSections.map((section) => (
            <div key={section.title} className="space-y-1">
              {!collapsed && (
                <Button
                  variant="ghost"
                  className="w-full justify-between px-2 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900"
                  onClick={() => toggleSection(section.title)}
                >
                  {section.title}
                  {openSections.includes(section.title) ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              )}
              
              <div className={cn(
                "space-y-1 overflow-hidden transition-all",
                !collapsed && !openSections.includes(section.title) && "h-0",
                collapsed && "h-auto"
              )}>
                {section.items.map((item) => (
                  <Button
                    key={item.route}
                    variant={currentRoute === item.route ? "secondary" : "ghost"}
                    className={cn(
                      "w-full justify-start",
                      collapsed ? "px-2" : "px-2",
                      currentRoute === item.route ? "bg-black/10 text-gray-900" : "text-gray-600 hover:text-gray-900 hover:bg-black/5",
                      "transition-colors"
                    )}
                    onClick={() => handleNavigation(item.route)}
                  >
                    <item.icon className={cn(
                      "h-5 w-5",
                      currentRoute === item.route ? "text-gray-900" : "text-gray-500"
                    )} />
                    {!collapsed && (
                      <>
                        <span className="ml-3">{item.name}</span>
                        {item.badge && (
                          <Badge variant="secondary" className="ml-auto bg-blue-500/10 text-blue-700">
                            {item.badge}
                          </Badge>
                        )}
                      </>
                    )}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>
    </aside>
  );
};

export default Sidebar;